/** Normativa por ejercicio (RF-03): valores incluidos en la aplicación y los que ha ajustado este negocio. */
import { useEffect, useState } from 'react';
import { Alert, Badge, Button, Group, NumberInput, Paper, Select, Stack, Table, Text, Textarea } from '@mantine/core';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';
import { avisarError, avisarOk, CampoEuros, CampoPorcentaje, EJERCICIO_ACTUAL, euros, OPCIONES_EJERCICIO } from '../comun';
import { useSesion } from '../sesion';
import type { ParametroNormativa } from '../tipos';

interface Tramo {
  hasta: number;
  importe: number;
}

export function formatearValor(p: Pick<ParametroNormativa, 'tipo'>, v: unknown): string {
  switch (p.tipo) {
    case 'centimos':
      return euros(v as number);
    case 'puntosBasicos':
      return `${((v as number) / 100).toLocaleString('es-ES')} %`;
    case 'entero':
      return `${v as number} años`;
    case 'tramos':
      return (v as Tramo[]).map((t) => `≤ ${euros(t.hasta)} → ${euros(t.importe)}`).join(' · ');
  }
}

function Editor({ p, valor, onCambio }: { p: ParametroNormativa; valor: unknown; onCambio: (v: unknown) => void }) {
  switch (p.tipo) {
    case 'centimos':
      return <CampoEuros valor={valor as number} onCambio={(v) => onCambio(v ?? 0)} w={180} />;
    case 'puntosBasicos':
      return <CampoPorcentaje valor={valor as number} onCambio={(v) => onCambio(v ?? 0)} w={140} />;
    case 'entero':
      return <NumberInput value={valor as number} min={1} max={30} onChange={(v) => onCambio(Number(v) || 1)} w={110} suffix=" años" />;
    case 'tramos': {
      const tramos = valor as Tramo[];
      const cambiar = (i: number, cambios: Partial<Tramo>) => onCambio(tramos.map((t, n) => (n === i ? { ...t, ...cambios } : t)));
      return (
        <Table withRowBorders={false} verticalSpacing={4}>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Rendimiento del año anterior hasta</Table.Th>
              <Table.Th>Minoración por trimestre</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {tramos.map((t, i) => (
              <Table.Tr key={i}>
                <Table.Td>
                  <CampoEuros valor={t.hasta} onCambio={(v) => cambiar(i, { hasta: v ?? 0 })} allowNegative w={160} />
                </Table.Td>
                <Table.Td>
                  <CampoEuros valor={t.importe} onCambio={(v) => cambiar(i, { importe: v ?? 0 })} w={160} />
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      );
    }
  }
}

function Fila({ p, ejercicio, editable, onGuardado }: { p: ParametroNormativa; ejercicio: number; editable: boolean; onGuardado: () => void }) {
  const [valor, setValor] = useState<unknown>(p.actual);
  const [nota, setNota] = useState(p.nota ?? '');
  const [guardando, setGuardando] = useState(false);
  useEffect(() => {
    setValor(p.actual);
    setNota(p.nota ?? '');
  }, [p.actual, p.nota]);
  const cambiado = JSON.stringify(valor) !== JSON.stringify(p.actual) || (p.editado && (p.nota ?? '') !== nota);

  const guardar = async () => {
    setGuardando(true);
    try {
      await api.put(`/negocio/normativa/${ejercicio}/${p.clave}`, { valor, nota: nota.trim() || null });
      avisarOk('Parámetro guardado');
      onGuardado();
    } catch (e) {
      avisarError(e);
    } finally {
      setGuardando(false);
    }
  };
  const restablecer = async () => {
    setGuardando(true);
    try {
      await api.delete(`/negocio/normativa/${ejercicio}/${p.clave}`);
      avisarOk('Restablecido al valor de la aplicación');
      onGuardado();
    } catch (e) {
      avisarError(e);
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Paper withBorder p="sm">
      <Group justify="space-between" align="flex-start" wrap="nowrap" gap="sm">
        <div style={{ minWidth: 0 }}>
          <Group gap="xs">
            <Text fw={600} size="sm">
              {p.nombre}
            </Text>
            {p.editado && <Badge color="blue" size="sm">Editado</Badge>}
            {!p.editado && p.provisional && <Badge color="orange" size="sm">Provisional: sin verificar</Badge>}
          </Group>
          <Text size="xs" c="dimmed">
            {p.ayuda}
          </Text>
        </div>
      </Group>
      <Stack gap="xs" mt="xs">
        {editable ? (
          <>
            <Editor p={p} valor={valor} onCambio={setValor} />
            <Textarea placeholder="Motivo o fuente del cambio (opcional)" autosize minRows={1} value={nota} onChange={(e) => setNota(e.currentTarget.value)} />
            <Group>
              <Button size="xs" onClick={guardar} loading={guardando} disabled={!cambiado}>
                Guardar
              </Button>
              {p.editado && (
                <Button size="xs" variant="subtle" color="gray" onClick={restablecer} loading={guardando}>
                  Restablecer ({formatearValor(p, p.porDefecto)})
                </Button>
              )}
            </Group>
          </>
        ) : (
          <Text size="sm">{formatearValor(p, p.actual)}</Text>
        )}
        <Text size="xs" c="dimmed">
          Por defecto: {formatearValor(p, p.porDefecto)} · Fuente: {p.fuente} · Verificado el {p.verificado}
          {p.nota && !editable ? ` · Nota: ${p.nota}` : ''}
        </Text>
      </Stack>
    </Paper>
  );
}

export function NormativaEditable() {
  const s = useSesion();
  const qc = useQueryClient();
  const [ejercicio, setEjercicio] = useState(String(EJERCICIO_ACTUAL));
  const consulta = useQuery({ queryKey: ['normativa', ejercicio], queryFn: () => api.get<ParametroNormativa[]>(`/negocio/normativa/${ejercicio}`) });
  const editable = s.negocio?.rol === 'gestor';
  const refrescar = () => {
    void qc.invalidateQueries({ queryKey: ['normativa'] });
    void qc.invalidateQueries({ queryKey: ['130'] });
    void qc.invalidateQueries({ queryKey: ['resumen'] });
  };
  return (
    <Stack maw={820}>
      <Alert color="blue" variant="light">
        La aplicación incluye la normativa de cada ejercicio con su fuente oficial. Aquí puedes corregir un valor si cambia durante el año o tu asesor te indica otro.
        {editable ? ' Cada cambio queda en el historial.' : ' Solo la persona con rol de gestor puede modificarla.'}
      </Alert>
      <Select label="Ejercicio" w={120} data={OPCIONES_EJERCICIO} value={ejercicio} onChange={(v) => setEjercicio(v ?? String(EJERCICIO_ACTUAL))} allowDeselect={false} />
      {(consulta.data ?? []).map((p) => (
        <Fila key={`${ejercicio}-${p.clave}`} p={p} ejercicio={Number(ejercicio)} editable={editable} onGuardado={refrescar} />
      ))}
    </Stack>
  );
}
